// SourceRef — citation anchor pointing an item back to source material.
//
// 19BG+2 Phase 5 (DEBT-AW-SOURCEREF). The fictional metric measures the
// fraction of items containing factual claims that the citation_trace lint
// can NOT verify against the source markdown. cs3052 stub Day 4 baseline:
// fictional metric 0.464 (post evaluator-v2 fix; v1 was 0.814 inflated). The
// only way to drive this below the score-1 cliff (0.30) is to make every
// LLM-emitted item carry a source pointer the lint rule can verify.
//
// Schema design (ChunkRef):
//   slotId    — the source slot the item came from (matches SourceSlot.id)
//   chunkIdx  — which chunk within the slot (sliding-window 1500-char chunks
//                in the LLM-native parse pipeline). Optional — slot-only
//                citation is acceptable for whole-page references.
//   snippet   — 20-200 chars copied verbatim from the cited chunk. The lint
//                rule does substring match (with whitespace normalization);
//                LLMs cannot fabricate a snippet that survives substring check
//                without actually pulling text from the source.
//   confidence — LLM self-assessed confidence 0-1. Items with confidence
//                <0.6 surface in the lint report for human review even if
//                the snippet matches.
//
// Why optional on the item schema (not required):
//   - Back-compat with pre-19BG+2 ingest packages already on disk
//   - Phase 5 rolls out per-stage (mcqs first, then fc/lectureNote · the
//     three kinds citation_trace actually evaluates); other stages adopt
//     incrementally
//   - audit-heal back-fills missing sourceRef via sourceRefFixOps in
//     AuditHealOutputSchema (chapter-bundle.ts)
//
// LLM_NATIVE_ARCHITECTURE compliance:
//   §2.1 (provenance) — sourceRef is the explicit chain back to authoritative
//                       material. Without it, items are floating claims.
//   §2.4 (passthrough) — host item schemas already use .passthrough(); when a
//                        field is added, downstream code keeps working.
//   §14.7 — verify with cs3052 + IB N=3 baseline. Decision rule: post-fix
//           fictional metric < pre-fix metric − 1σ_baseline counts as trend.
import { z } from 'zod';
/**
 * 19BH+2 Day 1 Round 3 (DEBT-19BH+1-PHASE3-BUNDLE-LATENCY-USAGE). cs3052 Run 3
 * raw output showed LLM emitting paragraph-length snippets (>200 chars · would
 * fail max(200) schema). Per LLM-native §14.3 post-LLM normalization, truncate
 * at last whitespace before 200 chars — the citation anchor still works since
 * the lint rule does substring-match (truncated prefix matches source too).
 *
 * Too-short snippets (<5 chars · Round 4 floor) remain a schema rejection —
 * that signals LLM couldn't actually quote source; better the schema retry
 * fires (with Fix D-2 prompt updates telling LLM to omit sourceRef when can't
 * quote) than coerce to invalid stub.
 *
 * 19BH+2 Day 2 Fix I-7 investigation outcome: explored adding null/{}/empty-
 * snippet → undefined coercion for "soft omit". Withdrawn after analysis —
 * Zod's `.nullish()` at host level only short-circuits null/undef BEFORE
 * preprocess runs, and the inner z.object() rejects undefined from preprocess
 * with a less-specific error than the field-level "snippet: Required" we want
 * the retry-hint to surface. Schema STAYS strict here; soft-omit happens at
 * the prompt layer (Fix D-2: "OMIT sourceRef entirely if can't quote 5+ chars")
 * and Fix A retry-with-hint relays the specific field error to LLM. This is
 * exactly the K6 / 陛下 5/14 ruling envelope: prompt + retry handle the
 * envelope of LLM behavior; we don't relax the contract to absorb stubs.
 */
function coerceChunkRefSnippet(obj) {
    if (typeof obj !== 'object' || obj === null)
        return obj;
    const o = obj;
    const snippet = o['snippet'];
    if (typeof snippet !== 'string' || snippet.length <= 200)
        return obj;
    const truncAt = snippet.lastIndexOf(' ', 197);
    const cut = truncAt >= 100 ? snippet.slice(0, truncAt) : snippet.slice(0, 197);
    return { ...o, snippet: cut + '...' };
}
export const ChunkRefSchema = z.preprocess(coerceChunkRefSnippet, z.object({
    /** Source slot id the item draws from (matches SourceSlot.id, e.g. `src_a1b2c3d4`). */
    slotId: z.string().min(1).max(120)
        .describe('The source slot id this citation anchors to. Matches a SourceSlot.id from the ingest payload.'),
    /** Optional chunk index within the slot (0-based). Omit for slot-level reference. */
    chunkIdx: z.number().int().min(0).nullish()
        .describe('Optional chunk index within the slot (0-based). Drives chunk-level navigation; omit for slot-wide reference.'),
    /**
     * Verbatim copy of 20-200 chars from the cited chunk. Whitespace-tolerant
     * substring match against the chunk content is the lint rule's hard gate
     * (lint/rules/source-ref.ts). Snippets that fail substring match flag the
     * item as a fictional citation candidate.
     */
    snippet: z.string().min(5).max(200)
        .describe('5-200 chars copied verbatim from the cited chunk. Lint rule substring-matches against source. Cannot be fabricated — must literally appear in the source markdown. 19BH+2 Day 1 Round 4: min relaxed 20→5 chars · domain-legitimate short citations like "P=NP?" / "DFA" / equation names ought to anchor too · lint substring match still gates quality.'),
    /** LLM self-assessed citation confidence (0-1). <0.6 surfaces for review. */
    confidence: z.number().min(0).max(1).nullish()
        .describe('Self-assessed confidence the snippet is the correct citation for this item. 1 = verbatim source quote · 0.6+ = paraphrase the snippet captures the original meaning · <0.6 = uncertain (lint surfaces for review).'),
}).passthrough());
//# sourceMappingURL=source-ref.js.map